import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Api, Code, Email, Refresh, Rule, Settings, Storage, Webhook } from '@mui/icons-material';
import { Box, Button, FormControl, InputLabel, MenuItem, Select, Typography } from '@mui/material';

import AddBlockButton from '../components/blocks/AddBlockButton';
import StatusButton from '../components/common/StatusButton';
import PrimaryLayout from '../components/layout/PrimaryLayout';
import DataTable from '../components/tables/DataTable';
import { Column } from '../components/tables/interfaces';
import { axiosRepoIntance } from '../services/api-client';
import { Branch, CommitStatus } from '../services/repo-service';
import { BLOCK_TYPE } from '../utils/constants/block';
import { OWNER, ReposByBlockType } from '../utils/constants/gitea';


const block_types = [
    {
        name: 'Decision/Evaluator',
        description: 'Nodes used to evaluate conditions and make decisions in the workflow. The flow forks into true/false paths based on the outcome.',
        icon: <Rule style={{ color: '#1976d2' }} />,
        path: BLOCK_TYPE.Evaluator,
    },
    {
        name: 'Transformer/Custom',
        description: 'Nodes that transform, aggregate, or apply custom logic to data within the workflow.',
        icon: <Code style={{ color: '#ff9800' }} />,
        path: BLOCK_TYPE.Transformer,
    },
    {
        name: 'Data Enricher - API',
        description: 'Fetches additional data by calling external APIs to enrich the workflow data.',
        icon: <Api style={{ color: '#009688' }} />,
        path: BLOCK_TYPE.DataEnricher_API,
    },
    {
        name: 'Data Enricher - DB Lookup',
        description: 'Queries databases to retrieve and enrich data in the workflow.',
        icon: <Storage style={{ color: '#8d6e63' }} />,
        path: BLOCK_TYPE.DataEnricher_DB_Lookup,
    },
    {
        name: 'Data Enricher - Graph Lookup',
        description: 'Performs graph database lookups to retrieve relationships or hierarchies.',
        icon: <Storage style={{ color: '#3f51b5' }} />,
        path: BLOCK_TYPE.DataEnricher_Graph_Lookup,
    },
    {
        name: 'Data Enricher - ML Prediction',
        description: 'Uses machine learning models to make predictions based on input data.',
        icon: <Code style={{ color: '#9c27b0' }} />,
        path: BLOCK_TYPE.DataEnricher_ML_Prediction,
    },
    {
        name: 'Push - Webhook',
        description: 'Sends data to an external service or system via a webhook.',
        icon: <Webhook style={{ color: '#f44336' }} />,
        path: BLOCK_TYPE.Push_Webhook,
    },
    {
        name: 'Push - DB',
        description: 'Inserts or updates data in a database as part of the workflow.',
        icon: <Storage style={{ color: '#2196f3' }} />,
        path: BLOCK_TYPE.Push_DB,
    },
    {
        name: 'Push - Queue',
        description: 'Pushes messages to a queue (e.g., Kafka, RabbitMQ) for asynchronous processing.',
        icon: <Webhook style={{ color: '#ff9800' }} />,
        path: BLOCK_TYPE.Push_Queue,
    },
    {
        name: 'Notification - Email',
        description: 'Sends email notifications based on workflow events.',
        icon: <Email style={{ color: '#4caf50' }} />,
        path: BLOCK_TYPE.Notification_Email,
    },
    {
        name: 'Notification - Slack',
        description: 'Sends Slack notifications to alert about workflow events.',
        icon: <Email style={{ color: '#673ab7' }} />,
        path: BLOCK_TYPE.Notification_Slack,
    },
    {
        name: 'System',
        description: 'System blocks are used to interact with the system and perform various operations.',
        icon: <Settings style={{ color: '#ddd' }} />,
        path: BLOCK_TYPE.System,
    }
];

const Blocks: React.FC = () => {

    const deploymentStatusMap: Record<string, any> = {};
    const columns: Column[] = [
        { id: "name", label: "Name", type: "string" },
        {
            id: "id",
            label: "Commit ID",
            type: "string",
            onCellClick: (value, row) =>
                navigate(`/blocks/${selectedBlock}/${row.name}/${row.id}`),
        },
        {
            id: "status",
            label: "Status",
            type: "string",
        },
        { id: "message", label: "Commit Message", type: "string" },
        { id: "author", label: "Author", type: "string" },
        { id: "created_at", label: "Created At", type: "datetime" },
    ];

    const navigate = useNavigate();
    const { type: block_type_param } = useParams<{ type: string }>();

    const [selectedBlock, setSelectedBlock] = useState<string>(block_type_param || block_types[0].path);
    const [flag, setFlag] = useState(false);

    const fetchData = async (query?: { pageSize?: number; nextPageToken?: string } | { page?: number; limit?: number }) => {
        if (query && ("page" in query || "limit" in query)) {
            try {
                const response = await axiosRepoIntance.repos.repoListBranches(
                    OWNER,
                    ReposByBlockType[selectedBlock],
                    query
                );
                const branches = response.data;
                await Promise.all(
                    branches.map(async (branch) => {
                        let temp: CommitStatus[] = [];
                        let deploymentStatus = "No Commits Found";
                        if (branch.commit?.id) {
                            try {
                                temp = await fetchRunnerStatus(
                                    branch.commit.id,
                                    ReposByBlockType[selectedBlock]
                                );
                                const status = temp.length > 0 ? temp[0].status : undefined;
                                const des = temp.length > 0 ? temp[0].description : "";
                                deploymentStatus = status || "No Commits Found";
                                if (deploymentStatus === "pending") {
                                    setFlag(true);
                                }
                                if (deploymentStatus === "success" && des === "Has been skipped") {
                                    deploymentStatus = "failure"
                                }
                            } catch (error) {
                                console.error("Error fetching runner status:", error);
                            }
                            deploymentStatusMap[branch.commit.id] = (
                                temp.length > 0 ? <StatusButton
                                    deploymentStatus={deploymentStatus}
                                    context={temp[0].context ? temp[0].context : ""}
                                    createdAt={temp[0].created_at ? temp[0].created_at : ""}
                                    updatedAt={temp[0].updated_at ? temp[0].updated_at : ""}
                                /> :
                                    <Typography variant="body2" color="textSecondary">
                                        No Status available, Please refresh.
                                    </Typography>
                            );
                        }
                    })
                );
                return branches;
            } catch (error) {
                console.error("Error fetching branches:", error);
                return [] as Branch[];
            }
        } else {
            return [] as Branch[];
        }
    };

    const dataExtractor = (branches: Branch[]) => {
        return branches.map((branch) => ({
            id: branch.commit?.id,
            name: branch.name,
            status: deploymentStatusMap[branch.commit?.id || ""],
            author: branch.commit?.author?.name,
            message: branch.commit?.message,
            created_at: branch.commit?.timestamp,
        }));
    };

    const fetchRunnerStatus = async (
        commit_id: string,
        branch_name: string,
        query?: {
            sort?: "oldest" | "recentupdate" | "leastupdate" | "leastindex" | "highestindex";
            page?: number;
            limit?: number;
        }
    ) => {
        try {
            const response = await axiosRepoIntance.repos.repoListStatusesByRef(
                OWNER,
                branch_name,
                commit_id,
                {
                    sort: query?.sort || "leastindex",
                    page: query?.page || 1,
                    limit: query?.limit || 1,
                }
            );
            console.log(response.data);
            return response.data;
        } catch (error) {
            console.error("Error fetching runner statuses:", error);
            return [];
        }
    };

    const handleBlockChange = (blockType: string) => {
        setSelectedBlock(blockType);
        navigate(`/blocks/${blockType}`, { replace: true });
    };

    useEffect(() => {
        const isValidType = block_types.some((block) => block.path === block_type_param);

        if (!block_type_param || !isValidType) {
            navigate(`/blocks/${block_types[0].path}`, { replace: true });
        }
        else if (block_type_param !== selectedBlock) {
            setSelectedBlock(block_type_param);
        }
    }, [block_type_param, navigate, selectedBlock]);

    return (
        <PrimaryLayout>
            <Box display="flex" justifyContent="space-between" alignItems="center">
                <FormControl fullWidth margin='normal' sx={{ mx: 2, maxWidth: 320 }}>
                    <InputLabel id="blocks-simple-select-label">Blocks</InputLabel>
                    <Select
                        labelId="blocks-simple-select-label"
                        id="blocks-simple-select"
                        value={selectedBlock}
                        label="Blocks"
                        onChange={(e) => handleBlockChange(e.target.value)}
                        renderValue={(selected) => {
                            const block = block_types.find(block => block.path === selected);
                            return <Box display='flex'>
                                {block?.icon} <Box component='span' ml={1}>{block?.name}</Box>
                            </Box>
                        }}
                        sx={{
                            display: 'flex',
                            alignItems: 'center'
                        }}
                    >
                        {block_types.map(item => (
                            <MenuItem key={item.name} value={item.path}>
                                {item.icon}
                                <Typography component='p' ml={2}>{item.name}</Typography>
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <Box display='flex' gap={2}>
                    <Button
                        variant="contained"
                        color='inherit'
                        startIcon={<Refresh />}
                        onClick={() => setFlag(!flag)}
                    >
                        Refresh
                    </Button>

                    <AddBlockButton />
                </Box>
            </Box>

            <Box sx={{ width: '100%' }}>
                <DataTable
                    columns={columns}
                    fetchData={fetchData}
                    dataExtractor={dataExtractor}
                />
            </Box>
        </PrimaryLayout>
    )
}

export default Blocks